import { memo, useCallback, useMemo } from 'react'

import { object, string, number, SchemaOf } from 'yup'
import moment from 'moment'
import { Formik, Form, Field } from 'formik'

import { Grid, Paper } from '@mui/material'

import { UploadContentType } from '@tabeeb/enums'
import { PageHeader, PageLayout } from '@collaborate-health/uikit'
import { uploadFile } from '@tabeeb/services/uploadService'
import { FormikRichTextEditor } from '@tabeeb/shared/forms'
import { maxLengthWithName, noWhitespaces, requiredFieldWithName } from '@tabeeb/shared/utils/validationErrorMessages'

import {
  AnnouncementScope,
  ArticlePublicationStatus,
  AnnouncementSeverity,
  AnnouncementSeverityDisplayName,
  ArticleContentType,
  AnnouncementPlatform,
  AnnouncementPlatformDisplayName,
} from '@tabeeb/modules/articles/enums'

import {
  AnnouncementCreateRequest,
  AnnouncementResponse,
} from '@tabeeb/contracts/microservices/swagger-article-service'

import { FormDateTimePicker, FormSelect, FormTextField } from '@collaborate-health/forms'
import ArticleSaveButton from '../../../components/ArticleSaveButton'

const announcementSchema: SchemaOf<AnnouncementCreateRequest> = object().shape({
  Name: string()
    .strict()
    .trim(noWhitespaces)
    .max(256, maxLengthWithName('Name', 256))
    .required(requiredFieldWithName('Name')),
  Content: object()
    .shape({
      Type: number().oneOf([ArticleContentType.Html, ArticleContentType.Pdf]),
      Content: string().required(),
    })
    .required(),
  Severity: number()
    .oneOf([AnnouncementSeverity.Information, AnnouncementSeverity.Notification, AnnouncementSeverity.Alert])
    .required(),
  Platform: number()
    .oneOf([AnnouncementPlatform.All, AnnouncementPlatform.Web, AnnouncementPlatform.Mobile])
    .required(),
  Status: number().oneOf([ArticlePublicationStatus.Draft, ArticlePublicationStatus.Published]).required(),
  Scope: number().oneOf([AnnouncementScope.System, AnnouncementScope.Corporation, AnnouncementScope.Tenant]),
  StartDate: string().required('Start date is required'),
  ExpiryDate: string()
    .required('End date is required')
    .test('is-after-start', "End date can't be before start date", function isAfterStart(value) {
      const { StartDate } = this.parent as AnnouncementCreateRequest
      return !value || !StartDate || new Date(value) >= new Date(StartDate)
    })
    .test('is-after-now', "End date can't be before the current date", function isAfterNow(value) {
      return !value || new Date(value) >= new Date()
    }),
})

export interface AnnouncementEditorPageProps {
  title: string
  initial?: AnnouncementResponse
  scope?: AnnouncementScope
  onSubmit: (announcement: AnnouncementCreateRequest) => void
}

const AnnouncementEditorPage: React.FC<AnnouncementEditorPageProps> = ({
  initial = {
    Id: 0,
    Name: '',
    Content: {
      Type: ArticleContentType.Html,
      Content: '',
    },
    Scope: AnnouncementScope.System,
    Severity: AnnouncementSeverity.Information,
    Status: ArticlePublicationStatus.Draft,
    Platform: AnnouncementPlatform.All,
    TenantId: null,
    StartDate: moment().format(),
    ExpiryDate: moment().add(7, 'days').format(),
  },
  title,
  scope,
  onSubmit,
}) => {
  const onUploadFile = useCallback(async ({ file, onProgress }) => {
    const { url, sas } = await uploadFile({
      file,
      uploadContentType: UploadContentType.ArticleDocument,
      onProgress,
      contentId: undefined,
      abortController: undefined,
    })

    return `${url.split('?')[0]}${sas.sasRead}`
  }, [])

  const initialValues = useMemo<AnnouncementCreateRequest>(() => {
    return {
      Id: initial.Id,
      Name: initial.Name,
      Content: initial.Content,
      Severity: initial.Severity,
      Status: initial.Status,
      Scope: scope || initial.Scope,
      Platform: initial.Platform ?? AnnouncementPlatform.All,
      TenantId: initial.TenantId,
      StartDate: initial.StartDate,
      ExpiryDate: initial.ExpiryDate,
    }
  }, [initial, scope])

  const onSubmitForm = useCallback(
    (data: AnnouncementCreateRequest) => {
      onSubmit({
        ...data,
        Name: data.Name,
        Content: data.Content,
        Severity: data.Severity,
        Status: data.Status,
        StartDate: data.StartDate,
        Platform: data.Platform,
        ExpiryDate: data.ExpiryDate,
      })
    },
    [onSubmit]
  )

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={announcementSchema}
      onSubmit={onSubmitForm}
    >
      {({ isSubmitting }) => {
        return (
          <Form style={{ display: 'flex', height: '100%', width: '100%' }} autoComplete='off'>
            <PageLayout background='default' spacing='page' component={Paper} maxWidth='lg'>
              <PageHeader title={title} actions={<ArticleSaveButton name='Status' />} />
              <Grid container wrap='nowrap' direction='column' spacing={2} flexGrow={1}>
                <Grid item container direction='row' spacing={2}>
                  <Grid item xs={12}>
                    <FormTextField disabled={isSubmitting} size='small' name='Name' label='Name' />
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <FormSelect
                      disabled={isSubmitting}
                      size='small'
                      name='Severity'
                      label='Severity'
                      options={[
                        AnnouncementSeverity.Information,
                        AnnouncementSeverity.Notification,
                        AnnouncementSeverity.Alert,
                      ].map((severity) => ({
                        name: AnnouncementSeverityDisplayName[severity],
                        value: severity,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={6} lg={3}>
                    <FormSelect
                      disabled={isSubmitting}
                      size='small'
                      name='Platform'
                      label='Platform'
                      options={[AnnouncementPlatform.All, AnnouncementPlatform.Web, AnnouncementPlatform.Mobile].map(
                        (platform) => ({
                          name: AnnouncementPlatformDisplayName[platform],
                          value: platform,
                        })
                      )}
                    />
                  </Grid>
                  <Grid item container xs={12} lg={6} spacing={2}>
                    <Grid item xs={6}>
                      <FormDateTimePicker
                        disabled={isSubmitting}
                        size='small'
                        name='StartDate'
                        label='Start'
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormDateTimePicker
                        disabled={isSubmitting}
                        size='small'
                        name='ExpiryDate'
                        label='End'
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name='Content.Content'
                    component={FormikRichTextEditor}
                    disabled={isSubmitting}
                    uploadsHandler={onUploadFile}
                  />
                </Grid>
              </Grid>
            </PageLayout>
          </Form>
        )
      }}
    </Formik>
  )
}

export default memo(AnnouncementEditorPage)

